import {
  InsuranceResponse,
  InsuranceVerificationAPIService,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/api-service/v1/insurance_verification_api.pb';
import {
  InsuranceVerificationAdapterService,
  InsuranceVerificationDetailsResponse,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { PersonService } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons_service.pb';
import {
  DataSource,
  GetDataSourceBySourceIDResponse,
} from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/datasource.pb';
import { IntegrationsService } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integrations_service.pb';
import { MockAPIRouter } from '@frontend/fetch';
import { serviceName } from '../../service';
import * as CallIntelTypes from '../../types';
import { extractCallFilterFromRequest, MockCallRecords } from '../call-recordings';
import { MockPersons } from '../persons';
import { BaseMockRouterContext, FeatureMockRouter } from './types';

export interface ContactPanelMockRouterContext extends BaseMockRouterContext {
  mockPersonsInstance: MockPersons;
  mockCallRecordsInstance: MockCallRecords;
}

export const ContactPanelMockRouter: FeatureMockRouter<ContactPanelMockRouterContext> = {
  addMockRouterEndpoints: (router, { mockPersonsInstance }) => {
    router.get('/photo/v1/person/:personId', async (_, params, next) => {
      if (params.personId.startsWith('demo')) {
        return MockAPIRouter.json({ error: 'Problem finding image file' }, 404);
      }
      next();
      return null;
    });

    router.get('/desktop/v1/person_extended/:personId', async (_, params, next) => {
      const { personId } = params;
      if (personId.startsWith('demo')) {
        const personExtendedResponse = mockPersonsInstance.getPersonExtended({ personId });
        return MockAPIRouter.json({ data: personExtendedResponse });
      }
      next();
      return null;
    });

    router.schemaEndpoint(PersonService.GetPersonLegacy, async (_, params, next) => {
      const { personId } = params;

      if (personId.startsWith('demo')) {
        const personData = mockPersonsInstance.getPersonV3({ personId });
        return MockAPIRouter.json({ data: personData });
      }
      next();
      return null;
    });

    router.schemaEndpoint(PersonService.SearchPersonsLegacy, async (request, _, next) => {
      const { searchParams } = new URL(request.url);
      const personId = searchParams.get('personId') ?? '';
      const householdId = searchParams.get('householdId') ?? '';

      if (personId.startsWith('demo') || householdId.startsWith('demo')) {
        const personData = mockPersonsInstance.getPersonV3({ personId, householdId });
        return MockAPIRouter.json({ persons: [personData], nextPageToken: '' });
      }
      next();
      return null;
    });

    router.schemaEndpoint(InsuranceVerificationAPIService.GetInsuranceByPersonID, async (_, params, next) => {
      if (params.personId.startsWith('demo')) {
        return MockAPIRouter.json({} as InsuranceResponse);
      }
      next();
      return null;
    });

    router.schemaEndpoint(
      InsuranceVerificationAdapterService.GetInsuranceVerificationDetails,
      async (request, _, next) => {
        const { searchParams } = new URL(request.url);
        const personId = searchParams.get('personId') ?? '';
        if (personId.startsWith('demo')) {
          return MockAPIRouter.json({
            coverage: {},
            memberId: '',
            onederfulPayerId: '',
          } as InsuranceVerificationDetailsResponse);
        }
        next();
        return null;
      }
    );

    router.get('/task/:householdId/set', async (_, params, next) => {
      if (params.householdId.startsWith('demo')) {
        return MockAPIRouter.json({
          birthdays: [],
          reminders: [],
          contact: [],
          overdue: [],
          collections: [],
          information: undefined,
          eyewear: undefined,
        });
      }
      next();
      return null;
    });

    router.schemaEndpoint(IntegrationsService.GetDataSourceBySourceID, async (_, params, next) => {
      if (params.sourceId && params.sourceId.startsWith('demo')) {
        return MockAPIRouter.json({
          dataSource: undefined,
        } as GetDataSourceBySourceIDResponse);
      }
      next();
      return null;
    });

    router.schemaEndpoint(IntegrationsService.GetDataSource, async (_, params, next) => {
      if (params.sourceId && params.sourceId.startsWith('demo')) {
        return MockAPIRouter.json({
          integration: {},
        } as DataSource);
      }
      next();
      return null;
    });

    router.get('/notes/', async (request, _, next) => {
      // url : /notes/?filter=household_id~demo-c3565210-b2d7-4429-87e1-439d50d2ddb5
      const { searchParams } = new URL(request.url);
      if (searchParams.get('filter')?.includes('household_id~demo')) {
        return MockAPIRouter.json([]);
      }
      next();
      return null;
    });

    router.get(/.*\/v1\/search\/invoices.*/, async (request, _, next) => {
      const { searchParams } = new URL(request.url);
      const personId = searchParams.get('personid') ?? '';
      if (personId.startsWith('demo')) {
        return MockAPIRouter.json({});
      }
      next();
      return null;
    });
  },
  getRequestInterceptors: ({ interceptorIdPrefix, mockPersonsInstance }) => [
    {
      id: `${interceptorIdPrefix}-person-recent-call`,
      interceptor: async (request: Request) => {
        const { pathname } = new URL(request.url);

        if (pathname !== '/call-intelligence/v2/person/calls/recent') {
          return request;
        }

        const requestBody: CallIntelTypes.RecentCallsIO['input'] = await request.clone().json();

        if (!requestBody?.personId || !requestBody.personId.startsWith('demo')) {
          return request;
        }

        const personData = mockPersonsInstance.getFilteredPersons({
          personId: requestBody.personId,
        })?.[0];

        if (!personData) {
          return request;
        }

        return new Request(request, {
          body: JSON.stringify({
            ...requestBody,
            isDemo: true,
            demoMetadata: {
              firstName: personData.firstName,
              lastName: personData.lastName,
              gender: personData.gender,
              vertical: 'default',
            },
            // Remove 'demo-' prefix from personId for the API call
            personId: personData.personId.replace('demo-', ''),
          } as CallIntelTypes.RecentCallsIO['input']),
        });
      },
    },
    {
      id: `${interceptorIdPrefix}-call-trends`,
      interceptor: async (request: Request) => {
        const { pathname } = new URL(request.url);
        if (pathname !== '/call-intelligence/v2/overview') {
          return request;
        }

        const requestBody: CallIntelTypes.GetCallsOverviewIO['input'] = await request.clone().json();

        // If overview call is not made by call trends section pass it to mock interceptor
        if (requestBody.isCallTrendDemo === undefined) {
          return request;
        }

        const { personId: personIdList } = extractCallFilterFromRequest(requestBody);

        if (requestBody.filter && personIdList) {
          requestBody.filter[CallIntelTypes.FilterTypeEnum.FILTER_BY_PERSON_ID].personId = personIdList.map((id) =>
            id.replace('demo-', '')
          );
        }

        return new Request(request, {
          body: JSON.stringify({
            ...requestBody,
            isCallTrendDemo: true,
          } as CallIntelTypes.GetCallsOverviewIO['input']),
        });
      },
    },
    {
      id: `${interceptorIdPrefix}-comm-history`,
      interceptor: async (request: Request) => {
        const { origin, pathname, searchParams } = new URL(request.url);
        if (pathname !== '/comm/history/v1') {
          return request;
        }

        const personId = searchParams.get('personId');

        if (!personId || !personId?.startsWith('demo')) return request;

        const personData = mockPersonsInstance.getFilteredPersons({
          personId,
        })?.[0];

        if (!personData) {
          return request;
        }
        const updatedParams = new URLSearchParams(searchParams);
        updatedParams.set('isDemo', 'true');
        updatedParams.set('demoPersonName', `${personData.firstName} ${personData.lastName}`);
        return new Request(`${origin}${pathname}?${updatedParams.toString()}`, request);
      },
    },
  ],
  invalidateQueries: (queryClient) => {
    queryClient.invalidateQueries([serviceName, 'GetCallsOverview']);
    // Other queries related to contact panel have 'demo' key checked in request so they do not need to be invalidated
  },
};
